import React, { useState, useEffect, useMemo } from "react"
import { UserPlus, Clock, Calendar, CheckCircle, Clock3, Search } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Layout } from "@/components/Layout"
import { useMyAccessRequests, useAccessRequests } from "@/hooks/useAccessRequests"
import { useUnits } from "@/hooks/useUnits"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import SolicitarAcessoModal from "@/components/SolicitarAcessoModal"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"

const Agenda = () => {
  const { profile } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  
  // Porteiros e administração veem todas as solicitações do condomínio
  // Moradores veem apenas suas próprias solicitações
  const shouldUseAllRequests = profile?.role === 'porteiro' || profile?.role === 'administracao' || profile?.role === 'sindico'
  
  const { data: myRequests = [], isLoading: isLoadingMy, error: errorMy } = useMyAccessRequests()
  const { data: allRequests = [], isLoading: isLoadingAll, error: errorAll } = useAccessRequests()
  const { data: units = [] } = useUnits()
  
  const accessRequests = shouldUseAllRequests ? allRequests : myRequests
  const isLoading = shouldUseAllRequests ? isLoadingAll : isLoadingMy

  const [accessType, setAccessType] = useState("")
  const [processedRequests, setProcessedRequests] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState("")


  const handleApproveRequest = async (request: any) => {
    // Marcar como processado IMEDIATAMENTE
    setProcessedRequests(prev => [...prev, request.id])
    console.log('✅ Aprovando solicitação:', request.id)
    
    try {
      const { error } = await supabase
        .from('access_requests')
        .update({
          status: 'approved',
          approved_by: profile?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', request.id)

      if (error) {
        console.error('❌ Erro na aprovação:', error)
        throw error
      }

      console.log('✅ Solicitação aprovada no banco')

      // Buscar dados do morador para WhatsApp e notificação
      const { data: requesterData } = await supabase
        .from('profiles')
        .select('whatsapp_phone, full_name')
        .eq('id', request.requester_id)
        .single()

      // Enviar WhatsApp
      if (requesterData?.whatsapp_phone) {
        try {
          console.log('📱 Enviando WhatsApp para:', requesterData.whatsapp_phone)
          await supabase.functions.invoke('send-whatsapp-notification', {
            body: {
              phone: requesterData.whatsapp_phone,
              recipientName: requesterData.full_name || 'Morador',
              accessType: request.type,
              personCompany: request.person_company,
              scheduledDate: request.scheduled_date,
              scheduledTime: request.scheduled_time,
              message: `🟢 *ACESSO LIBERADO*\n\n✅ Sua solicitação de acesso foi aprovada!\n\n👤 *Pessoa/Empresa:* ${request.person_company}\n📅 *Data:* ${request.scheduled_date}\n⏰ *Horário:* ${request.scheduled_time}\n\nO acesso foi liberado pela portaria.`
            }
          })
        } catch (whatsappError) {
          console.error('📱 Erro no WhatsApp:', whatsappError)
        }
      }

      // Criar notificação no app para o morador
      try {
        await supabase
          .from('read_notifications')
          .insert({
            user_id: request.requester_id,
            notification_id: `access_approved_${request.id}`,
            expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
          })
        console.log('🔔 Notificação criada para o morador')
      } catch (notificationError) {
        console.error('🔔 Erro ao criar notificação:', notificationError)
      }

      // Cache clearing mais agressivo
      queryClient.removeQueries({ queryKey: ["access_requests"] })
      queryClient.removeQueries({ queryKey: ["my_access_requests"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      
      // Refetch manual
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ["access_requests"] })
        queryClient.refetchQueries({ queryKey: ["my_access_requests"] })
        queryClient.refetchQueries({ queryKey: ["notifications"] })
      }, 500)

      toast({
        title: "✅ Acesso liberado!",
        description: "O morador foi notificado via WhatsApp e app.",
      })

      // Remover da lista de processados após sucesso
      setProcessedRequests(prev => prev.filter(id => id !== request.id))
      
    } catch (error) {
      console.error('❌ Erro geral:', error)
      setProcessedRequests(prev => prev.filter(id => id !== request.id))
      toast({
        title: "❌ Erro",
        description: "Não foi possível aprovar a solicitação.",
        variant: "destructive",
      })
    }
  }

  const handleRejectRequest = async (request: any) => {
    // Marcar como processado IMEDIATAMENTE
    setProcessedRequests(prev => [...prev, request.id])
    console.log('❌ Rejeitando solicitação:', request.id)
    
    try {
      const { error } = await supabase
        .from('access_requests')
        .update({
          status: 'rejected',
          approved_by: profile?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', request.id)

      if (error) {
        console.error('❌ Erro na rejeição:', error)
        throw error
      }

      console.log('❌ Solicitação rejeitada no banco')

      // Cache clearing mais agressivo
      queryClient.removeQueries({ queryKey: ["access_requests"] })
      queryClient.removeQueries({ queryKey: ["my_access_requests"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      
      // Refetch manual
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ["access_requests"] })
        queryClient.refetchQueries({ queryKey: ["my_access_requests"] })
        queryClient.refetchQueries({ queryKey: ["notifications"] })
      }, 500)

      toast({
        title: "❌ Acesso rejeitado",
        description: "A solicitação foi rejeitada.",
      })

      // Remover da lista de processados após sucesso
      setProcessedRequests(prev => prev.filter(id => id !== request.id))
      
    } catch (error) {
      console.error('❌ Erro geral:', error)
      setProcessedRequests(prev => prev.filter(id => id !== request.id))
      toast({
        title: "❌ Erro",
        description: "Não foi possível rejeitar a solicitação.",
        variant: "destructive",
      })
    }
  }

  // Debug logs
  console.log('📊 Agenda Debug:', {
    profileRole: profile?.role,
    shouldUseAllRequests,
    accessRequestsCount: accessRequests.length,
    processedRequests,
    isLoading
  })

  // Funções utilitárias (declaradas antes do useMemo)
  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'visitante': return '👤'
      case 'delivery': return '📦'
      case 'servico': return '🔧'
      case 'mudancas': return '📦'
      case 'outros': return '🏢'
      default: return '👤'
    }
  }

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case 'visitante': return 'Visitante'
      case 'delivery': return 'Delivery'
      case 'servico': return 'Serviço'
      case 'mudancas': return 'Mudanças'
      case 'outros': return 'Outros'
      default: return 'Visitante'
    }
  }

  // Função para buscar dados do morador da solicitação
  const getRequesterUnit = (requesterId: string) => {
    const unit = units.find(u => u.owner?.id === requesterId)
    return unit ? `${unit.building ? unit.building + ' - ' : ''}Unidade ${unit.number}` : 'N/A'
  }

  // Filtro de pesquisa 
  const filteredRequests = useMemo(() => {
    if (!searchTerm) return accessRequests

    return accessRequests.filter(request => {
      const searchLower = searchTerm.toLowerCase()
      const requesterUnit = getRequesterUnit(request.requester_id)
      
      return (
        request.person_company?.toLowerCase().includes(searchLower) ||
        request.document?.toLowerCase().includes(searchLower) ||
        requesterUnit.toLowerCase().includes(searchLower) ||
        getTipoText(request.type).toLowerCase().includes(searchLower)
      )
    })
  }, [accessRequests, searchTerm, units])

  // Estatísticas das solicitações
  const today = new Date()
  const todayStr = format(today, 'yyyy-MM-dd')
  
  const requestsToday = filteredRequests.filter(request => 
    format(new Date(request.created_at), 'yyyy-MM-dd') === todayStr
  )
  
  const pendingRequests = filteredRequests.filter(request => 
    request.status === 'pending'
  )

  const approvedRequests = filteredRequests.filter(request => 
    request.status === 'approved'
  )

  const recentRequests = filteredRequests.slice(0, 10)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'rejected': return 'bg-gray-100 text-gray-600'
      case 'completed': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Liberado'
      case 'pending': return 'Pendente'
      case 'rejected': return 'Rejeitado'
      case 'completed': return 'Concluído'
      default: return 'Desconhecido'
    }
  }


  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Liberar Acesso</h1>
            <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Liberar Acesso</h1>
            <p className="text-gray-600 dark:text-gray-400">Solicite liberação de acesso para visitantes, deliveries e serviços</p>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Hoje</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{requestsToday.length}</div>
              <p className="text-xs text-muted-foreground">solicitações criadas</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
              <Clock3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingRequests.length}</div>
              <p className="text-xs text-muted-foreground">aguardando aprovação</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aprovadas</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{approvedRequests.length}</div>
              <p className="text-xs text-muted-foreground">solicitações aprovadas</p>
            </CardContent>
          </Card>
        </div>

        {/* Formulário para moradores */}
        {profile?.role === 'morador' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Solicitar Liberação de Acesso
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tipo de Acesso</label>
                  <Select value={accessType} onValueChange={setAccessType}>
                    <SelectTrigger className="border-2 border-blue-500 focus:border-blue-600 focus:ring-blue-500">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border shadow-lg z-50">
                      <SelectItem value="visitante">👤 Visitante</SelectItem>
                      <SelectItem value="delivery">📦 Delivery</SelectItem>
                      <SelectItem value="servico">🔧 Serviço</SelectItem>
                      <SelectItem value="mudancas">📦 Mudanças</SelectItem>
                      <SelectItem value="outros">🏢 Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-end">
                  <SolicitarAcessoModal defaultType={accessType}>
                    <Button className="w-full" disabled={!accessType}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      {accessType ? `Solicitar ${getTipoText(accessType)}` : 'Selecione o tipo de acesso'}
                    </Button>
                  </SolicitarAcessoModal>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Campo de pesquisa para porteiros */}
        {shouldUseAllRequests && (
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Pesquisar por nome, documento, casa ou unidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        {/* Lista de Solicitações */}
        <Card>
          <CardHeader>
            <CardTitle>
              {shouldUseAllRequests ? 'Solicitações do Condomínio' : 'Minhas Solicitações'}
              {shouldUseAllRequests && searchTerm && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({filteredRequests.length} encontradas)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentRequests.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Nenhuma solicitação encontrada.
              </div>
            ) : (
              <div className="space-y-3">
                {recentRequests.map((request) => {
                  const isProcessing = processedRequests.includes(request.id)
                  const isApproved = request.status === 'approved'
                  const isRejected = request.status === 'rejected'
                  const isPending = request.status === 'pending'
                  
                  return (
                    <div
                      key={request.id}
                      className={`p-4 border rounded-lg transition-all ${
                        isApproved ? 'bg-green-50 border-green-200 opacity-80' :
                        isRejected ? 'bg-gray-50 border-gray-300 opacity-80' :
                        isProcessing ? 'bg-yellow-50 border-yellow-200' :
                        'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">{getTipoIcon(request.type)}</div>
                          <div>
                            <p className="font-medium">{request.person_company}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>{getTipoText(request.type)}</span>
                              {request.document && (
                                <>
                                  <span>•</span>
                                  <span>Doc: {request.document}</span>
                                </>
                              )}
                              {shouldUseAllRequests && (
                                <>
                                  <span>•</span>
                                  <span>{getRequesterUnit(request.requester_id)}</span>
                                </>
                              )}
                              <span>•</span>
                              <span>
                                {format(new Date(request.scheduled_date), "dd/MM/yyyy", { locale: ptBR })} às {request.scheduled_time}
                              </span>
                              {request.recurrence !== 'nenhuma' && (
                                <>
                                  <span>•</span>
                                  <span>{request.recurrence}</span>
                                </>
                              )}
                              {request.approved_at && (
                                <>
                                  <span>•</span>
                                  <span className="text-xs">
                                    {isApproved ? 'Aprovado' : 'Rejeitado'} em {format(new Date(request.approved_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          {/* Status visual */}
                          <div className="text-right">
                            {isProcessing && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                ⏳ Processando...
                              </span>
                            )}
                            {!isProcessing && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                {isApproved && '✅'} {isRejected && '❌'} {isPending && '⏰'} {getStatusText(request.status)}
                              </span>
                            )}
                          </div>

                          {/* Botões de ação */}
                          {shouldUseAllRequests && isPending && !isProcessing && (
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApproveRequest(request)}
                              >
                                Liberar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-600 text-red-600 hover:bg-red-50"
                                onClick={() => handleRejectRequest(request)}
                              >
                                Rejeitar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}

export default Agenda